/**
 * UI Preferences Context
 * Global settings for UI preferences like showing/hiding summary sections
 * Persists to database for cross-session storage
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiGet, apiPut } from '../lib/api';
import { auth } from '../lib/auth';

interface UIPreferences {
  showPageSummary: boolean;
}

interface UIPreferencesContextType extends UIPreferences {
  updatePreferences: (prefs: Partial<UIPreferences>) => void;
  isLoading: boolean;
}

const defaultPreferences: UIPreferences = {
  showPageSummary: true,
};

const UIPreferencesContext = createContext<UIPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = 'luma-ui-preferences';

export function UIPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UIPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPreferences = async () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setPreferences({ ...defaultPreferences, ...parsed });
        } catch (e) {
          console.error('Failed to parse UI preferences from localStorage:', e);
        }
      }

      if (auth.getToken()) {
        try {
          const result = await apiGet<{ showPageSummary: boolean }>('/settings/ui-preferences');
          if (result.success && result.data) {
            const dbPrefs = { ...defaultPreferences, ...result.data };
            setPreferences(dbPrefs);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dbPrefs));
          }
        } catch (e) {
          console.error('Failed to load UI preferences from database:', e);
        }
      }
      setIsLoading(false);
    };

    loadPreferences();
  }, []);

  const updatePreferences = async (newPrefs: Partial<UIPreferences>) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    if (auth.getToken()) {
      try {
        await apiPut('/settings/ui-preferences', updated);
      } catch (e) {
        console.error('Failed to save UI preferences to database:', e);
      }
    }
  };

  return (
    <UIPreferencesContext.Provider
      value={{
        ...preferences,
        updatePreferences,
        isLoading,
      }}
    >
      {children}
    </UIPreferencesContext.Provider>
  );
}

export function useUIPreferences() {
  const context = useContext(UIPreferencesContext);
  if (!context) {
    throw new Error('useUIPreferences must be used within UIPreferencesProvider');
  }
  return context;
}
